import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import {
   Grid,
   TextField,
   Box,
   FormControl,
   InputLabel,
   Select,
   MenuItem,
   Button,
   Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../../Config';
import { toast } from 'react-toastify';
import { getAuthToken } from '../../Redux/Service/FetchBaseQuery';
import StyledDashboardLayout from '../style';
import { useGetProvidersQuery } from '../../Redux/Service/ProviderApi';
import API from '../../Web/Instance';
import { useGetCurrenciesQuery } from '../../Redux/Service/CurrencyApi';

const EditSEORate = () => {
   const { id } = useParams();
   const navigate = useNavigate();
   const [loading, setLoading] = useState(true);
   const [symbol, setSymbol] = useState('$');
   const [initialValues, setInitialValues] = useState({
      serviceProviderId: '',
      currencyId: '',
      client: '',
      taskName: '',
      hourlyRateText: '',
      description: '',
   });

   const {
      data: providerData,
      isLoading: isLoadingForProvider,
      isError: isErrorForProvider,
      error: errorForProvider,
   } = useGetProvidersQuery();

   const { data: currencies, isLoading: isLoadingForCurrency } = useGetCurrenciesQuery();

   useEffect(() => {
      const fetchSeo = async () => {
         setLoading(true);
         try {
            const res = await axios.get(`${API_URL}hourlyrate/seo-rate/${id}`, {
               headers: {
                  Authorization: `Bearer ${getAuthToken()}`,
               },
            });
            const data = res.data?.data;
            if (data) {
               setInitialValues({
                  serviceProviderId: data.serviceProvider?.id ?? '',
                  currencyId: data.currency?.id ?? '',
                  client: data.client ?? '',
                  taskName: data.taskName ?? '',
                  hourlyRateText: data.hourlyRateText ?? '',
                  description: data.description ?? '',
               });

               const selCur = data.currency?.symbol ?? data.currency?.code;
               if (selCur) setSymbol(selCur);
            }
         } catch (error) {
            toast.error('Failed to load SEO rate');
         } finally {
            setLoading(false);
         }
      };

      if (id) fetchSeo();
      // eslint-disable-next-line
   }, [id]);

   const currencySymbolSet = (currencyId, setFieldValue) => {
      setFieldValue('currencyId', currencyId);
      const selectedCurrency = currencies?.response?.find((val) => val.id === currencyId);
      setSymbol(selectedCurrency?.symbol ?? selectedCurrency?.code ?? '$');
   };

   const validationSchema = Yup.object({
      serviceProviderId: Yup.string().required('Service Provider is required'),
      currencyId: Yup.string().required('Currency is required'),
      client: Yup.string()
         .required('Client is required')
         .max(50, 'Client should be atmost 50 characters long'),
      taskName: Yup.string()
         .required('Task Name is required')
         .max(100, 'Task Name should be atmost 100 characters long'),
      hourlyRateText: Yup.number()
         .positive('Hourly rate must be a positive number')
         .required('Hourly rate is required')
         .max(99999, 'Hourly rate should be atmost 99999'),
      description: Yup.string().max(250, 'Description should have atmost 250 characters'),
   });

   const handleSubmit = async (values, { setSubmitting }) => {
      try {
         const token = getAuthToken();
         const payload = {
            serviceProviderId: parseInt(values.serviceProviderId),
            client: values.client,
            taskName: values.taskName,
            hourlyRateText: parseFloat(values.hourlyRateText),
            currencyId: parseInt(values.currencyId),
            description: values.description,
         };

         const response = await axios.patch(`${API_URL}hourlyrate/update-seo-rate/${id}`, payload, {
            headers: {
               Authorization: `Bearer ${token}`,
               'Content-Type': 'application/json',
            },
         });

         toast.success(response?.data?.message || 'SEO Hourly Rate updated successfully');
         navigate('/hourly-rate/seo-rate-list');
      } catch (error) {
         toast.error(error?.response?.data?.message || 'Failed to update SEO hourly rate');
      } finally {
         setSubmitting(false);
      }
   };

   if (loading || isLoadingForProvider || isLoadingForCurrency) {
      return <div>Loading...</div>;
   }

   return (
      <StyledDashboardLayout>
         <div className="content-wrapper">
            <div className="content-header">
               <div className="container-fluid">
                  <div className="d-flex justify-content-between mb-2">
                     <div className="pageTitle">
                        <h1 className="m-0">Edit SEO Hourly Rate</h1>
                     </div>
                     <div className="btnWrap">
                        <Link to="/hourly-rate/seo-rate-list" className="btn addBtn">
                           <ArrowBackIcon /> Back
                        </Link>
                     </div>
                  </div>
               </div>
            </div>

            <div className="card customCard p-3 m-3">
               <Formik
                  enableReinitialize
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
               >
                  {({ values, setFieldValue, touched, errors, isSubmitting, handleBlur }) => (
                     <Form>
                        <Box className="card-body" sx={{ padding: '15px 0' }}>
                           <Grid container spacing={2}>
                              <Grid item md={6} xs={12}>
                                 {isErrorForProvider ? (
                                    <div>Error: {errorForProvider?.message}</div>
                                 ) : (
                                    <FormControl fullWidth>
                                       <InputLabel id="service-provider-label">
                                          Service Provider
                                       </InputLabel>
                                       <Field
                                          name="serviceProviderId"
                                          as={Select}
                                          labelId="service-provider-label"
                                          label="Service Provider"
                                          onBlur={handleBlur}
                                       >
                                          <MenuItem value="" disabled>
                                             Select Service Provider
                                          </MenuItem>
                                          {providerData?.response?.map((val, index) => (
                                             <MenuItem key={index} value={val.id}>
                                                {val.name}
                                             </MenuItem>
                                          ))}
                                       </Field>
                                       <ErrorMessage
                                          name="serviceProviderId"
                                          className="error-message"
                                          component="div"
                                          style={{ color: 'red' }}
                                       />
                                    </FormControl>
                                 )}
                              </Grid>

                              <Grid item xs={12} md={6}>
                                 <FormControl fullWidth>
                                    <InputLabel id="currency-label">Currency</InputLabel>
                                    <Field
                                       label="Currency"
                                       name="currencyId"
                                       as={Select}
                                       labelId="currency-label"
                                       onChange={(event) =>
                                          currencySymbolSet(event.target.value, setFieldValue)
                                       }
                                    >
                                       <MenuItem value="" disabled>
                                          Select Currency
                                       </MenuItem>
                                       {currencies?.response?.map((val, index) => (
                                          <MenuItem key={index} value={val.id}>
                                             {val.name} ({val.code})
                                          </MenuItem>
                                       ))}
                                    </Field>
                                    <ErrorMessage
                                       name="currencyId"
                                       component="div"
                                       style={{ color: 'red' }}
                                    />
                                 </FormControl>
                              </Grid>

                              <Grid item xs={12} md={6}>
                                 <Field
                                    fullWidth
                                    label="Client"
                                    name="client"
                                    as={TextField}
                                    variant="outlined"
                                    onBlur={handleBlur}
                                    error={touched.client && Boolean(errors.client)}
                                    helperText={touched.client && errors.client}
                                 />
                              </Grid>

                              <Grid item xs={12} md={6}>
                                 <Field
                                    fullWidth
                                    label="Task Name"
                                    name="taskName"
                                    as={TextField}
                                    variant="outlined"
                                    onBlur={handleBlur}
                                    error={touched.taskName && Boolean(errors.taskName)}
                                    helperText={touched.taskName && errors.taskName}
                                 />
                              </Grid>

                              <Grid item xs={12} md={6}>
                                 <Field
                                    fullWidth
                                    label={`Hourly Rate (${symbol})`}
                                    name="hourlyRateText"
                                    as={TextField}
                                    variant="outlined"
                                    type="number"
                                    inputProps={{ min: '0', step: '0.01' }}
                                    onBlur={handleBlur}
                                    error={touched.hourlyRateText && Boolean(errors.hourlyRateText)}
                                    helperText={touched.hourlyRateText && errors.hourlyRateText}
                                 />
                              </Grid>

                              <Grid item xs={12}>
                                 <Field
                                    fullWidth
                                    label="Description"
                                    name="description"
                                    as={TextField}
                                    variant="outlined"
                                    multiline
                                    rows={3}
                                    onBlur={handleBlur}
                                    error={touched.description && Boolean(errors.description)}
                                    helperText={touched.description && errors.description}
                                 />
                                 {values?.description && (
                                    <Typography sx={{ fontSize: '12px', color: 'grey' }}>
                                       {values?.description?.length} characters
                                    </Typography>
                                 )}
                              </Grid>

                              <Grid item xs={12}>
                                 <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    disabled={isSubmitting}
                                    className="commonBtn"
                                 >
                                    {isSubmitting ? 'Saving...' : 'Update'}
                                 </Button>
                              </Grid>
                           </Grid>
                        </Box>
                     </Form>
                  )}
               </Formik>
            </div>
         </div>
      </StyledDashboardLayout>
   );
};

export default EditSEORate;
